/**
 * ScamBlocker AI — API Anahtarları Sayfası (/api-keys)
 * Anahtar listeleme, oluşturma, iptal etme.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Key, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '@/shared/lib/api/client';
import { API } from '@/shared/lib/api/endpoints';
import styles from './ApiKeysPage.module.css';

interface ApiKey {
  id: string;
  name: string;
  prefix: string; // İlk 8 karakter
  created_at: string;
  last_used_at?: string;
  revoked: boolean;
}

export function ApiKeysPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data, isLoading } = useQuery<{ keys: ApiKey[] }>({
    queryKey: ['api-keys'],
    queryFn: async () => {
      const { data } = await apiClient.get(API.org.apiKeys);
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (name: string) => {
      const { data } = await apiClient.post(API.org.apiKeys, { name });
      return data as { key: string; id: string };
    },
    onSuccess: (data) => {
      setNewSecret(data.key);
      setNewName('');
      setCreating(false);
      qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(API.org.revokeKey(id));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const copySecret = async () => {
    if (!newSecret) return;
    await navigator.clipboard.writeText(newSecret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const activeKeys = data?.keys.filter((k) => !k.revoked) ?? [];
  const revokedKeys = data?.keys.filter((k) => k.revoked) ?? [];

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <Key size={22} className={styles.titleIcon} />
          <h1 className={styles.title}>{t('apiKeys.title')}</h1>
        </div>
        <button type="button" className={styles.createBtn} onClick={() => setCreating((v) => !v)}>
          <Plus size={16} /> Yeni Anahtar
        </button>
      </div>

      {/* Yeni anahtar formu */}
      {creating && (
        <div className={styles.createForm}>
          <input
            className={styles.nameInput}
            type="text"
            placeholder={t('apiKeys.namePlaceholder')}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim()) createMutation.mutate(newName.trim());
            }}
          />
          <button
            type="button"
            className={styles.confirmBtn}
            disabled={!newName.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate(newName.trim())}
          >
            {createMutation.isPending ? t('apiKeys.creating') : t('apiKeys.create')}
          </button>
          <button type="button" className={styles.cancelBtn} onClick={() => setCreating(false)}>
            {t('apiKeys.cancel')}
          </button>
        </div>
      )}

      {/* Yeni gizli anahtar göster (bir kez) */}
      {newSecret && (
        <div className={styles.secretBanner}>
          <p className={styles.secretWarning}>
            ⚠️ {t('apiKeys.secretWarningPrefix')}{' '}
            <strong>{t('apiKeys.secretWarningEmphasis')}</strong> {t('apiKeys.secretWarningSuffix')}
          </p>
          <div className={styles.secretRow}>
            <code className={styles.secretCode}>{newSecret}</code>
            <button type="button" className={styles.copyBtn} onClick={copySecret}>
              {copied ? <Check size={15} /> : <Copy size={15} />}
              {copied ? t('apiKeys.copied') : t('apiKeys.copy')}
            </button>
          </div>
          <button type="button" className={styles.dismissBtn} onClick={() => setNewSecret(null)}>
            {t('apiKeys.close')}
          </button>
        </div>
      )}

      {isLoading && (
        <div className={styles.center}>
          <span className={styles.spinner} />
        </div>
      )}

      {/* Aktif anahtarlar */}
      {!isLoading && (
        <>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {t('apiKeys.activeKeys', { count: activeKeys.length })}
            </h2>
            {activeKeys.length === 0 ? (
              <p className={styles.empty}>{t('apiKeys.noActiveKeys')}</p>
            ) : (
              <div className={styles.keyList}>
                {activeKeys.map((k) => (
                  <div key={k.id} className={styles.keyCard}>
                    <div className={styles.keyInfo}>
                      <span className={styles.keyName}>{k.name}</span>
                      <code className={styles.keyPrefix}>{k.prefix}…</code>
                      <span className={styles.keyDate}>
                        {t('apiKeys.createdAt', {
                          date: new Date(k.created_at).toLocaleDateString('tr-TR'),
                        })}
                      </span>
                      {k.last_used_at && (
                        <span className={styles.keyDate}>
                          {t('apiKeys.lastUsed', {
                            date: new Date(k.last_used_at).toLocaleString('tr-TR'),
                          })}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className={styles.revokeBtn}
                      title={t('apiKeys.revoke')}
                      onClick={() => {
                        if (
                          confirm(`"${k.name}" anahtarını iptal etmek istediğinize emin misiniz?`)
                        ) {
                          revokeMutation.mutate(k.id);
                        }
                      }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {revokedKeys.length > 0 && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle} style={{ opacity: 0.5 }}>
                {t('apiKeys.revokedKeys', { count: revokedKeys.length })}
              </h2>
              <div className={styles.keyList}>
                {revokedKeys.map((k) => (
                  <div key={k.id} className={`${styles.keyCard} ${styles.revoked}`}>
                    <div className={styles.keyInfo}>
                      <span className={styles.keyName}>{k.name}</span>
                      <code className={styles.keyPrefix}>{k.prefix}…</code>
                    </div>
                    <span className={styles.revokedBadge}>{t('apiKeys.revokedBadge')}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
